import React, { FormEvent, useState } from "react";
import { Box } from "src/components/Box";
import { Input } from "src/components/Input";
import { Text } from "src/components/Text";
import { Info } from "@asphalt-react/iconpack";
import { Button } from "src/components/Button";
import { useTranslation } from "src/hooks/useTranslation";
import styled, { css } from "styled-components";
import { observer } from "mobx-react-lite";
import { MediaQueries } from "src/constants/MediaQueries";
import Checkbox from "@asphalt-react/checkbox";
import { LoadingOverlay } from "src/components/LoadingOverlay";
import { BankInfoAlert } from "./BankInfo.alert";
import IconTuner from "@asphalt-react/icontuner";
import { usePassportStore } from "src/stores/Passport.store";
import { BankSelector } from "src/components/BankSelector";
import { useBankInfoStore } from "src/modules/forms/BankInfo/BankInfo.state";
import { FilePickerWithPreview } from "src/components/FilePicker";
import { eventDispatcher } from "src/helpers/eventEmitter";
import { PASSPORT_ROUTES } from "src/constants/routes";
import { useHistory } from "react-router-dom";
import { Breakpoint } from "src/components/Breakpoint";
import { setCookie, CookieNames } from "src/helpers/cookies";
import { errFormatter } from "src/helpers/errorFormatter";
import { useErrorModalStore } from "src/components/ErrorModal/ErrorModal.store";
import { AlertBox } from "src/components/AlertBox";
import { usePassportImprovements } from "../../../hooks";

const BoxColumnEl = styled(Box)`
  ${MediaQueries.screen.medium.minWidth} {
    display: flex;
    align-items: baseline;

    > div {
      flex: 1;

      :first-child {
        padding-right: ${(p) => p.theme.layout.margin.xl}px;
      }

      :only-child {
        flex: 0.5;
      }
    }
  }
`;

const BoxButtonEl = styled(Box)`
  margin-top: 42px;

  > button {
    min-width: 128px;
  }

  ${MediaQueries.screen.medium.minWidth} {
    ${(p) =>
      p &&
      css`
        border-top: 2px solid ${p.theme.colors.borders.light};
        padding-top: ${p.theme.layout.margin.xl}px;
      `}
  }
`;

const BoxNotesContainerEl = styled(Box)`
  align-items: baseline;

  ${MediaQueries.screen.medium.minWidth} {
    align-items: center;
  }
`;

const BoxCheckboxEL = styled(Box)`
  > span {
    display: block;

    > label {
      padding: 0;
    }
  }
`;

const FilePickerWrapperEl = styled.div`
  ${MediaQueries.screen.medium.minWidth} {
    max-width: 432px;
  }
`;

export const PassportBankInfoForm = observer(() => {
  const { push } = useHistory();
  const translate = useTranslation();
  const passportStore = usePassportStore();
  const { outlet } = passportStore;
  const bankInfoStore = useBankInfoStore();
  const {
    selectedBank,
    bankAccountHolder,
    bankAccountNumber,
    verified,
    accountNameMismatch,
    maskedBankName,
    agreeToDataMismatchPolicy,
    disableNextButton,
    loadingState,
    invalidBankNumber,
  } = bankInfoStore;
  const errorModalStore = useErrorModalStore();
  const {
    isImprovementEnabled,
    enableImprovementsAndRedirect,
  } = usePassportImprovements();
  const [isErrorUploadBankBook, setIsErrorUploadBankBook] = useState(false);

  const handleOnUpload = async (file: File) => {
    eventDispatcher.publish({ action: "ob_upload_bankbook" });
    try {
      setIsErrorUploadBankBook(false);
      const uploadUrl = await bankInfoStore.uploadFileBankInfo(
        passportStore.authUser.id,
        file
      );
      bankInfoStore.setBankBookUploadUrl(uploadUrl);
      eventDispatcher.publish({ action: "ob_upload_bankbook_success" });
    } catch (error) {
      const { status, message } = errFormatter(error);
      eventDispatcher.publish({
        action: "ob_upload_bankbook_error",
        payload: {
          fileType: file.type,
          message: message,
          httpStatus: status,
        },
      });
      setIsErrorUploadBankBook(true);
    }
  };

  const onFormSubmit = async (event: FormEvent) => {
    event?.preventDefault();
    try {
      if (!isImprovementEnabled) {
        await enableImprovementsAndRedirect();
        return;
      }

      // Name mismatch not allowed for coporate users
      const hasVerified = outlet.isCorporateMerchant
        ? verified && !accountNameMismatch
        : verified;

      if (!hasVerified) {
        eventDispatcher.publish({ action: "ob_bankinfo_validate" });
        const {
          verified,
          accountNameMismatch,
        } = await bankInfoStore.validate();
        if (!verified) {
          eventDispatcher.publish({ action: "ob_bankinfo_invalid_acc_error" });
        }
        if (accountNameMismatch) {
          eventDispatcher.publish({ action: "ob_bankinfo_invalid_name_error" });
        }
      } else {
        await bankInfoStore.postData();
        eventDispatcher.publish({ action: "ob_bankinfo_next" });
        setCookie(CookieNames.PassportStep, PASSPORT_ROUTES.termsConditions);
        push(PASSPORT_ROUTES.termsConditions);
      }
    } catch (err) {
      const { status, message } = errFormatter(err);
      eventDispatcher.publish({
        action: "ob_bankinfo_validate",
        payload: {
          message: message,
          httpStatus: status,
        },
      });
      errorModalStore.showAlert();
      errorModalStore.retryCta = onFormSubmit;
    }
  };

  const isLoading = loadingState.has.validate || loadingState.has.postData;

  const showInputForm = !verified || accountNameMismatch;

  const showNextButton = (() => {
    if (outlet.isCorporateMerchant) {
      return verified && !accountNameMismatch;
    }
    return verified;
  })();

  const disabledVerifyButton = (() => {
    if (outlet.isCorporateMerchant) {
      return !selectedBank || !bankAccountHolder || !bankAccountNumber;
    }
    return !selectedBank || !bankAccountNumber;
  })();

  return (
    <Box as="form" onSubmit={onFormSubmit}>
      <Breakpoint medium up>
        <Text marginBottomXl fontSize="xlarge" fontWeight="demi">
          {translate("bank_info.title")}
        </Text>
      </Breakpoint>
      <Box marginBottomXl>
        <AlertBox color="info" icon={<Info />}>
          {translate(`bank_info.alert.${outlet.businessType}`)}
        </AlertBox>
      </Box>
      {showInputForm && (
        <Box>
          <BoxColumnEl>
            <Box marginBottomL>
              <BankSelector
                onChange={(bank: any) =>
                  (bankInfoStore.selectedBank = bank.value)
                }
                label={translate(
                  `bank_info.${outlet.businessType}.bank_name.label`
                )}
                placeholder={translate(
                  `bank_info.${outlet.businessType}.bank_name.placeholder`
                )}
                defaultValue={selectedBank}
              />
            </Box>
            <Box marginBottomL>
              <Input
                label={translate(
                  `bank_info.${outlet.businessType}.bank_account.label`
                )}
                placeholder={translate(
                  `bank_info.${outlet.businessType}.bank_account.placeholder`
                )}
                onChange={(e) => {
                  bankInfoStore.submitted = false;
                  bankInfoStore.bankAccountNumber = e.target.value;
                }}
                error={invalidBankNumber}
                helperText={
                  invalidBankNumber
                    ? translate("bank_info.bank_account.error")
                    : translate("bank_info.bank_account.helper")
                }
                value={bankAccountNumber}
                pattern="^[0-9]*$"
                inputMode="numeric"
              />
            </Box>
          </BoxColumnEl>

          {outlet.isCorporateMerchant && (
            <BoxColumnEl>
              <Box marginBottomL>
                <Input
                  label={translate("bank_info.corporate.bank_holder.label")}
                  placeholder={translate(
                    "bank_info.corporate.bank_holder.placeholder"
                  )}
                  onChange={(e) =>
                    (bankInfoStore.bankAccountHolder = e.target.value)
                  }
                  error={accountNameMismatch}
                  helperText={
                    accountNameMismatch
                      ? translate("bank_info.corporate.bank_holder.error")
                      : translate("bank_info.corporate.bank_holder.helper")
                  }
                  value={bankAccountHolder}
                  pattern="^[a-zA-Z0-9 .]*$"
                />
              </Box>
            </BoxColumnEl>
          )}
        </Box>
      )}

      {/* Show if name mismatch, for indiv only */}
      {accountNameMismatch && !outlet.isCorporateMerchant && (
        <BankInfoAlert color="danger">
          <BoxColumnEl>
            <Input
              readOnly
              label={translate("bank_info.validate.owner_name")}
              value={outlet.json.id_name}
            />
            <Input
              readOnly
              label={translate("bank_info.validate.bank_account_name")}
              value={maskedBankName}
            />
          </BoxColumnEl>
        </BankInfoAlert>
      )}

      {/* Show if successful and no name mismatch */}
      {verified && !accountNameMismatch && (
        <BankInfoAlert color="primary">
          <BoxColumnEl>
            <Input
              readOnly
              label={translate("bank_info.individual.bank_name.label")}
              value={selectedBank.toUpperCase()}
            />
            <Input
              readOnly
              label={translate("bank_info.individual.bank_account.label")}
              value={bankInfoStore.bankAccountNumber}
            />
          </BoxColumnEl>
          <BoxColumnEl>
            <Input
              readOnly
              label={translate("bank_info.validate.owner_name")}
              value={outlet.idName}
            />
            <Input
              readOnly
              label={translate("bank_info.validate.bank_account_name")}
              value={maskedBankName}
            />
          </BoxColumnEl>
        </BankInfoAlert>
      )}

      {accountNameMismatch && !outlet.isCorporateMerchant && (
        <>
          <Box>
            <BoxNotesContainerEl flex marginBottomL>
              <IconTuner info size="m">
                <Info />
              </IconTuner>
              <Text marginLeftM fontSize="small">
                {translate("bank_info.instruction_document")}
              </Text>
            </BoxNotesContainerEl>

            <BoxCheckboxEL flex marginBottomL>
              <Checkbox
                id="agreement"
                size="s"
                checked={agreeToDataMismatchPolicy}
                onChange={() =>
                  (bankInfoStore.agreeToDataMismatchPolicy = !agreeToDataMismatchPolicy)
                }
              />
              <Text marginLeftM flexAuto fontSize="medium" fontWeight="demi">
                <label htmlFor="agreement">
                  {translate("bank_info.agreement.label")}
                </label>
              </Text>
            </BoxCheckboxEL>
          </Box>
        </>
      )}

      {agreeToDataMismatchPolicy && (
        <FilePickerWrapperEl>
          <FilePickerWithPreview
            onChange={handleOnUpload}
            onError={(error) =>
              eventDispatcher.publish({
                action: "ob_upload_bankbook_error",
                payload: { message: error.message },
              })
            }
            previewUrl={bankInfoStore.bankBookUploadUrl}
            isLoading={passportStore.loadingState.has.uploadFile}
            onDelete={() => (bankInfoStore.bankBookUploadUrl = "")}
            isError={isErrorUploadBankBook}
          />
        </FilePickerWrapperEl>
      )}

      <BoxButtonEl flex justifyBetween>
        <Button
          outline
          onClick={() => {
            eventDispatcher.publish({ action: "ob_bankinfo_back" });
            push(PASSPORT_ROUTES.businessInfo);
          }}
        >
          {translate("bank_info.cta.back")}
        </Button>
        {!showNextButton && (
          <Button type="submit" disabled={disabledVerifyButton}>
            {translate("bank_info.cta.validate")}
          </Button>
        )}
        {showNextButton && (
          <Button type="submit" disabled={disableNextButton}>
            {translate("bank_info.cta.next")}
          </Button>
        )}
      </BoxButtonEl>

      <LoadingOverlay text={translate("bank_info.loading")} show={isLoading} />
    </Box>
  );
});
